import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { DARK_THEME, LIGHT_THEME, type ThemeMode } from '../theme/theme';

interface ThemeColors {
  transparent: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  backgroundSecondary: string;
  reversedBackground: string;
  topBarBackdrop: string;
  modalBackground?: string;
  modalOverlay?: string;
  loadingOverlay?: string;
  gradientBackground?: string;
  /** Live-streaming top scrim gradient (CSS `linear-gradient` string). */
  liveStreamingTopOverlay: string;
  /** Live-streaming bottom scrim gradient (CSS `linear-gradient` string). */
  liveStreamingBottomOverlay: string;
  surface: string;
  black: string;
  border: string;
  disabled: string;
  disabledTab: string;
  white: string;
  exclusive: string;
  exclusiveTransparent: string;
  /** Last-gift banner pill background — translucent dark (Flutter native `backgroundColorLight @ .2`). */
  lastGiftPillBackground: string;
  whiteLight: string;
  whiteLightTransparent: string;
  danger: string;
  dangerHover: string;
  warning: string;
  activeRed: string;
  notificationUnread: string;
  blue: string;
  lightBlue: string;
  toggleActive: string;
  toggleInactive: string;
  chat: {
    ownMessage: string;
    peerMessage: string;
    replyMessage: string;
  };
  shadow: string;
  text: {
    primary: string;
    secondary: string;
    selected: string;
  };
  icon: {
    primary: string;
    secondary: string;
    selected: string;
    unselected: string;
  };
  button: {
    primary: string;
    secondary: string;
    ghost: string;
    danger: string;
    round: string;
  };
  stepper: {
    progressInactive: string;
    progressActive: string;
    progressCompleted: string;
  };
  separator: string;
}

interface ThemeContextType {
  theme: ThemeColors;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get initial theme from localStorage or default to 'system'
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved as ThemeMode) || 'system';
  });

  // Initialize theme based on system preference or saved mode
  const [theme, setTheme] = useState<ThemeColors>(() => {
    const saved = localStorage.getItem('themeMode') as ThemeMode;
    const mode = saved || 'system';

    if (mode === 'light') {
      return LIGHT_THEME;
    } else if (mode === 'dark') {
      return DARK_THEME;
    } else {
      // System mode - check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? DARK_THEME : LIGHT_THEME;
    }
  });

  // Determine effective theme based on mode
  useEffect(() => {
    const getEffectiveTheme = (): ThemeColors => {
      if (themeMode === 'light') {
        return LIGHT_THEME;
      } else if (themeMode === 'dark') {
        return DARK_THEME;
      } else {
        // System mode - check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? DARK_THEME : LIGHT_THEME;
      }
    };

    setTheme(getEffectiveTheme());

    // Listen for system theme changes when in system mode
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        setTheme(mediaQuery.matches ? DARK_THEME : LIGHT_THEME);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  // Apply theme to document root and CSS custom properties
  useEffect(() => {
    const root = document.documentElement;

    // Apply to body for backwards compatibility
    document.body.style.backgroundColor = theme.background;
    document.body.style.color = theme.text.primary;

    // Set CSS custom properties for use in CSS files
    root.style.setProperty('--theme-background', theme.background);
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-surface', theme.surface);
    root.style.setProperty('--theme-border', theme.border);
    root.style.setProperty('--theme-accent', theme.accent);
    root.style.setProperty('--theme-exclusive', theme.exclusive);
    root.style.setProperty('--theme-text-primary', theme.text.primary);
    root.style.setProperty('--theme-text-secondary', theme.text.secondary);
    root.style.setProperty('--theme-text-selected', theme.text.selected);
    root.style.setProperty('--theme-icon-primary', theme.icon.primary);
    root.style.setProperty('--theme-icon-secondary', theme.icon.secondary);
    root.style.setProperty('--theme-icon-selected', theme.icon.selected);
    root.style.setProperty('--theme-icon-unselected', theme.icon.unselected);
    root.style.setProperty('--theme-white', theme.white);
    root.style.setProperty('--theme-danger', theme.danger);
    root.style.setProperty('--theme-danger-hover', theme.dangerHover);
  }, [theme]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
